import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import { estimatesApi } from '@/api/client';
import { formatCurrency } from '@medical-counsellor/shared';

export function ActualBillPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Form State
    const [admissionId, setAdmissionId] = useState('');
    const [surgeryDate, setSurgeryDate] = useState('');
    const [dischargeDate, setDischargeDate] = useState('');
    const [actualStayDays, setActualStayDays] = useState<number>(0);
    const [actualRoomType, setActualRoomType] = useState('');
    const [billingReference, setBillingReference] = useState('');

    const [lines, setLines] = useState<Array<{
        id: string; // temporary id for key
        sectionName: string;
        itemName: string;
        quantity: number;
        unitPrice: number;
        lineTotal: number;
    }>>([]);

    const { data: estimate, isLoading } = useQuery({
        queryKey: ['estimate', id],
        queryFn: () => estimatesApi.getById(id!),
        enabled: !!id,
    });

    // Initialize form
    useEffect(() => {
        if (estimate) {
            const actual = estimate.actualProcedure;

            if (actual) {
                setAdmissionId(actual.admissionId || '');
                setSurgeryDate(actual.surgeryDate ? new Date(actual.surgeryDate).toISOString().split('T')[0] : '');
                setDischargeDate(actual.dischargeDate ? new Date(actual.dischargeDate).toISOString().split('T')[0] : '');
                setActualStayDays(actual.actualStayDays ?? estimate.stayDays);
                setActualRoomType(actual.actualRoomType ?? estimate.roomType);
                setBillingReference(actual.billingReference || '');

                if (actual.lines && actual.lines.length > 0) {
                    setLines(actual.lines.map((l: any) => ({
                        id: l.id,
                        sectionName: l.sectionName || '',
                        itemName: l.itemName,
                        quantity: l.quantity,
                        unitPrice: l.unitPrice,
                        lineTotal: l.lineTotal,
                    })));
                } else {
                    setLines(estimate.lines.map((l: any) => ({
                        id: l.id,
                        sectionName: l.sectionName,
                        itemName: l.itemName,
                        quantity: l.quantity,
                        unitPrice: l.unitPrice,
                        lineTotal: l.lineTotal,
                    })));
                }
            } else {
                setLines(estimate.lines.map((l: any) => ({
                    id: l.id,
                    sectionName: l.sectionName,
                    itemName: l.itemName,
                    quantity: l.quantity,
                    unitPrice: l.unitPrice,
                    lineTotal: l.lineTotal,
                })));
            }
        }
    }, [estimate]);

    const saveMutation = useMutation({
        mutationFn: (data: any) => estimatesApi.saveActuals(id!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['estimate', id] });
            navigate(`/estimates/${id}`);
        },
    });

    const handleSave = () => {
        saveMutation.mutate({
            admissionId,
            surgeryDate: surgeryDate || undefined,
            dischargeDate: dischargeDate || undefined,
            actualStayDays: Number(actualStayDays),
            actualRoomType,
            billingReference,
            lines: lines.map(l => ({
                sectionName: l.sectionName,
                itemName: l.itemName,
                quantity: Number(l.quantity),
                unitPrice: Number(l.unitPrice),
            })),
        });
    };

    const updateLine = (idx: number, field: string, value: any) => {
        const newLines = [...lines];
        const line = { ...newLines[idx], [field]: value };

        if (field === 'quantity' || field === 'unitPrice') {
            line.lineTotal = Number(line.quantity) * Number(line.unitPrice);
        }

        newLines[idx] = line;
        setLines(newLines);
    };

    const removeLine = (idx: number) => {
        setLines(lines.filter((_, i) => i !== idx));
    };

    const addLine = () => {
        setLines([
            ...lines,
            {
                id: `new-${Date.now()}`,
                sectionName: 'Additional Costs',
                itemName: '',
                quantity: 1,
                unitPrice: 0,
                lineTotal: 0,
            },
        ]);
    };

    const calculateTotal = () => lines.reduce((sum, l) => sum + l.lineTotal, 0);

    if (isLoading) return <div className="p-8">Loading...</div>;
    if (!estimate) return <div className="p-8">Estimate not found</div>;

    return (
        <div className="space-y-6 animate-fade-in pb-20">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sticky top-0 bg-slate-50 z-10 py-4 -mx-4 px-4 sm:static sm:py-0 sm:mx-0 sm:px-0">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-200 rounded-lg transition-colors">
                        <ArrowLeft className="w-5 h-5 text-slate-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">Record Actual Bill</h1>
                        <p className="text-slate-600">
                            {estimate.referenceNumber} • {estimate.patientName}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="btn btn-secondary btn-md">
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="btn btn-primary btn-md shadow-lg shadow-primary-500/20"
                        disabled={saveMutation.isPending}
                    >
                        <Save className="w-4 h-4" />
                        {saveMutation.isPending ? 'Saving...' : 'Save Actuals'}
                    </button>
                </div>
            </div>

            <div className="max-w-5xl mx-auto">
                <div className="card shadow-lg border-slate-200">
                    <div className="card-header bg-slate-50 border-b border-slate-100 py-4">
                        <h2 className="text-lg font-semibold text-slate-800">Bill Details & Line Items</h2>
                    </div>

                    <div className="p-6 border-b border-slate-100">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="form-control">
                                <label className="label">Surgery Date</label>
                                <input
                                    type="date"
                                    className="input focus:ring-2 focus:ring-primary-100"
                                    value={surgeryDate}
                                    onChange={(e) => setSurgeryDate(e.target.value)}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">Discharge Date</label>
                                <input
                                    type="date"
                                    className="input focus:ring-2 focus:ring-primary-100"
                                    value={dischargeDate}
                                    onChange={(e) => setDischargeDate(e.target.value)}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">Actual Stay (Days)</label>
                                <input
                                    type="number"
                                    className="input focus:ring-2 focus:ring-primary-100"
                                    value={actualStayDays}
                                    onChange={(e) => setActualStayDays(Number(e.target.value))}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">Actual Room Type</label>
                                <input
                                    type="text"
                                    className="input focus:ring-2 focus:ring-primary-100"
                                    value={actualRoomType}
                                    onChange={(e) => setActualRoomType(e.target.value)}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">Billing Reference / Bill No.</label>
                                <input
                                    type="text"
                                    className="input focus:ring-2 focus:ring-primary-100"
                                    value={billingReference}
                                    onChange={(e) => setBillingReference(e.target.value)}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label">Admission ID</label>
                                <input
                                    type="text"
                                    className="input focus:ring-2 focus:ring-primary-100"
                                    value={admissionId}
                                    onChange={(e) => setAdmissionId(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="p-6 bg-slate-50/50">
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h3 className="text-base font-semibold text-slate-900">Line Items</h3>
                                <p className="text-sm text-slate-500">Add items from the final bill</p>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="text-right bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm">
                                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-2">Total Amount</span>
                                    <span className="text-xl font-bold text-primary-700">{formatCurrency(calculateTotal())}</span>
                                </div>
                                <button onClick={addLine} className="btn btn-secondary btn-md shadow-sm bg-white hover:bg-slate-50">
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Item
                                </button>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {lines.map((line, idx) => (
                                <div key={line.id || idx} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-primary-200 transition-all duration-200 group">
                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                                        {/* Section */}
                                        <div className="md:col-span-3">
                                            <label className="text-[10px] font-bold text-slate-400 mb-1.5 block uppercase tracking-wider">Section</label>
                                            <input
                                                type="text"
                                                className="input input-sm w-full bg-slate-50 border-slate-200 focus:bg-white focus:border-primary-300 transition-colors"
                                                value={line.sectionName}
                                                onChange={(e) => updateLine(idx, 'sectionName', e.target.value)}
                                                placeholder="Section"
                                            />
                                        </div>

                                        {/* Item Name */}
                                        <div className="md:col-span-4">
                                            <label className="text-[10px] font-bold text-slate-400 mb-1.5 block uppercase tracking-wider">Item Name</label>
                                            <input
                                                type="text"
                                                className="input input-sm w-full font-medium border-slate-200 focus:border-primary-300 transition-colors"
                                                value={line.itemName}
                                                onChange={(e) => updateLine(idx, 'itemName', e.target.value)}
                                                placeholder="Item description"
                                            />
                                        </div>

                                        {/* Qty */}
                                        <div className="md:col-span-1">
                                            <label className="text-[10px] font-bold text-slate-400 mb-1.5 block uppercase tracking-wider">Qty</label>
                                            <input
                                                type="number"
                                                className="input input-sm w-full text-right border-slate-200 focus:border-primary-300"
                                                value={line.quantity}
                                                onChange={(e) => updateLine(idx, 'quantity', Number(e.target.value))}
                                                min="0"
                                            />
                                        </div>

                                        {/* Unit Price */}
                                        <div className="md:col-span-2">
                                            <label className="text-[10px] font-bold text-slate-400 mb-1.5 block uppercase tracking-wider">Unit Price</label>
                                            <input
                                                type="number"
                                                className="input input-sm w-full text-right border-slate-200 focus:border-primary-300"
                                                value={line.unitPrice}
                                                onChange={(e) => updateLine(idx, 'unitPrice', Number(e.target.value))}
                                                min="0"
                                            />
                                        </div>

                                        {/* Total & Action */}
                                        <div className="md:col-span-2 flex items-center justify-between pl-4 border-l border-slate-100 h-full pb-1">
                                            <div>
                                                <label className="text-[10px] font-bold text-slate-400 mb-0.5 block text-right uppercase tracking-wider">Total</label>
                                                <div className="text-right font-bold text-slate-900 text-sm">
                                                    {formatCurrency(line.lineTotal)}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeLine(idx)}
                                                className="btn btn-ghost btn-xs w-8 h-8 p-0 rounded-full text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all opacity-0 group-hover:opacity-100 ml-2"
                                                title="Remove line"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {lines.length === 0 && (
                                <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-slate-200">
                                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
                                        <Plus className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-lg font-medium text-slate-900">No items added</h3>
                                    <p className="text-slate-500 mb-4 max-w-sm mx-auto">Start adding items from the actual hospital bill to track variance against the estimate.</p>
                                    <button onClick={addLine} className="btn btn-primary btn-sm">
                                        <Plus className="w-4 h-4 mr-1" />
                                        Add First Item
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
